// Ant design imports
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleTwoTone, LoadingOutlined, PlusOutlined } from "@ant-design/icons"
// Model imports
// Service imports
import { Employee, PriceIntervalEnum, rolesStringArray } from "@finway-group/shared/lib/models"
import { Button, Empty, Table, Tag, Tooltip } from "antd"
import { ColumnsType } from "antd/lib/table"
import httpStatus from "http-status"
// React imports
import React, { useState } from "react"
import { User as UserIcon } from "react-feather"
// Other imports
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
// Redux imports
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useDebouncedCallback } from "use-debounce/lib"

import DataUploadAlert from "Components/alert/dataUpload.alert"
import Filter from "Components/filter/filter"
import FilterSection from "Components/filterSection"
import Icon from "Components/icon"
import { ErrorTable } from "Components/layout/errorTable"
import Loading from "Components/loading"
import { EmployeeModal } from "Components/modals/employee.modal"
import NoSearchDataFound from "Components/NoSearchDataFound"
import SearchInput from "Components/searchInput"
import UserImage from "Components/UserImage"
import { formatEmployeeAbsenceDate } from "Features/pages/employees/employeeUtils"
import { useModal } from "Shared/context/modal.context"
import { useAreCardsActivated } from "Shared/hooks/corporate.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useIsFreeVersion } from "Shared/hooks/featureFlags.hooks"
import { useFetchTable, useTable, useTableFilterAndSearchLoading } from "Shared/hooks/table.hooks"
import { useTeams } from "Shared/hooks/team.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { CorporateService, NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
// Store imports
import { ThunkDispatchResult } from "Shared/store"
import { setEmployee } from "Shared/store/actions/employee/employeeActions"
import { refetchTable, updateTableOnChange, updateTableSearch } from "Shared/store/actions/tables/tableActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { EmployeeWithExpenseData } from "Shared/store/reducers/tableReducer"
import { insertIf } from "Shared/utils"
import { applyFilter } from "Shared/utils/filter.utils"
import { getEmployeeLocations } from "Shared/utils/getters"
import { getItemById, isItemDeleted } from "Shared/utils/helpers"
import { getTooltipPopupContainer } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { getSortOrderForColumn, isTableFilterApplied } from "Shared/utils/table.utils"
import { ORANGE } from "App.scss"

import PriceLabel from "../../../components/priceLabel"
import LoadingData from "Components/loading.data"

export const Employees: React.FC<{}> = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch: ThunkDispatchResult = useDispatch()
    const { showModal } = useModal()
    const isFreeVersion = useIsFreeVersion()

    const [isEnrolling, setIsEnrolling] = useStateIfMounted(false)
    const [isFilterModalShowing, setIsFilterModalShowing] = useState<boolean>(false)

    const employeesTable = useTable<EmployeeWithExpenseData>(TablesEnum.EMPLOYEES)
    const loggedInUser = useLoggedInEmployeeProfile()
    const cardsActive = useAreCardsActivated()
    const hasUnenrolledEmployees = cardsActive && employeesTable.data.docs.find((emp: Employee) => !emp?.activeCompanyProfile?.weavrData?.userId)
    const teams = useTeams()
    const employees = useEmployees(true)
    const locations = getEmployeeLocations(employees)
    const pageLoading = useTableFilterAndSearchLoading(TablesEnum.EMPLOYEES)
    useFetchTable(TablesEnum.EMPLOYEES)

    const handleSearch = useDebouncedCallback((searchString) => dispatch(updateTableSearch(TablesEnum.EMPLOYEES, searchString)), 800)

    const nameFormatter = (data: Employee) => (
        <div className="flex items-center image-loader gap-10">
            <UserImage user={data} size="large" />
            <div className="flex flex-col truncate">
                <div className="flex flex-row items-center mb-3">
                    <p className="font-bold leading-tight mb-0 truncate">
                        {data.firstName} {data.lastName}
                    </p>
                    {data.settings && data.settings.security?.tfaEnabled && <Tag className="ant-tag-blue ml-10">2fa</Tag>}
                    {data.isInAbsenceOfLeave() && (
                        <Tooltip title={formatEmployeeAbsenceDate(data)}>
                            <Tag className="ant-tag-gray inline-flex ml-5" icon={<Icon icon="calendar_cancel" className="pr-4" />}>
                                {t("label:employee_absence.absent")}
                            </Tag>
                        </Tooltip>
                    )}
                </div>
                <p className="text-xs text-text leading-none mb-0 truncate">{data.position}</p>
            </div>
        </div>
    )

    const enrollAll = () => {
        setIsEnrolling(true)
        CorporateService.enrollAll()
            .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:cards.all_enrolled.title"), t("notification:cards.all_enrolled.message")))
            .catch((err) => {
                if (err.response?.status === httpStatus.UNPROCESSABLE_ENTITY) {
                    NotificationService.send(NotificationTypeEnum.WARNING, t("notification:cards.not_all_enrolled.title"), t("notification:cards.not_all_enrolled.message"))
                } else {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
                }
            })
            .finally(() => setIsEnrolling(false))
    }

    const columns: ColumnsType<EmployeeWithExpenseData> = [
        {
            key: "employee",
            title: t("label:employee"),
            className: "pl-10",
            ellipsis: true,
            sortOrder: getSortOrderForColumn(employeesTable, "employee"),
            sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
            render: nameFormatter,
            width: 250,
        },
        {
            key: "location",
            title: t("label:location"),
            ellipsis: true,
            sortOrder: getSortOrderForColumn(employeesTable, "location"),
            sorter: (a, b) => a.location.localeCompare(b.location),
            width: 140,
            render: (employee) => <div className="truncate">{employee.location}</div>,
        },
        ...insertIf(loggedInUser.activeCompanyProfile.isAdmin && !isFreeVersion, {
            title: t("label:role"),
            key: "activeCompanyProfile.role",
            sortOrder: getSortOrderForColumn(employeesTable, "activeCompanyProfile.role"),
            sorter: (a: Employee, b: Employee) => {
                const labelA = t(`label:employee_labels.role.${rolesStringArray[a.activeCompanyProfile?.role]?.toLocaleLowerCase()}`)
                const labelB = t(`label:employee_labels.role.${rolesStringArray[b.activeCompanyProfile?.role]?.toLocaleLowerCase()}`)
                return labelA.localeCompare(labelB)
            },
            width: 120,
            render: (employee: Employee) => <span>{t(`input:employee.roles.${rolesStringArray[employee?.activeCompanyProfile?.role]?.toLowerCase()}`)}</span>,
        }),
        {
            title: t("label:team"),
            key: "activeCompanyProfile.team",
            sortOrder: getSortOrderForColumn(employeesTable, "activeCompanyProfile.team"),
            sorter: (a, b) => {
                return getItemById(teams, a?.activeCompanyProfile?.team)?.teamName.localeCompare(getItemById(teams, b.activeCompanyProfile.team)?.teamName)
            },
            width: 170,
            render: (employee) => {
                return (
                    <div className="inline-flex space-x-4">
                        <span>{getItemById(teams, employee?.activeCompanyProfile?.team)?.teamName}</span>
                        {isItemDeleted(teams, `${employee?.activeCompanyProfile?.team}`) && (
                            <Tooltip autoAdjustOverflow={false} placement="right" title={t("info:team_deleted")} color={ORANGE}>
                                <ExclamationCircleTwoTone twoToneColor={ORANGE} className="pt-4" />
                            </Tooltip>
                        )}
                    </div>
                )
            },
        },
        ...insertIf(loggedInUser.activeCompanyProfile.isAdmin && cardsActive, {
            title: t("label:cards.card_program"),
            key: "cardProgram",
            width: 120,
            className: "pr-10",
            sortOrder: getSortOrderForColumn(employeesTable, "cardProgram"),
            sorter: (a: Employee, b: Employee) => {
                if (a.activeCompanyProfile.weavrData?.userId && !b.activeCompanyProfile.weavrData?.userId) return 1
                else if (!a.activeCompanyProfile.weavrData?.userId && b.activeCompanyProfile.weavrData?.userId) return -1
                else return 0
            },
            render: (employee: Employee) => {
                return employee.activeCompanyProfile.weavrData?.userId ? (
                    <div className="flex items-left justify-left">
                        <Tag className="flex items-left" icon={<CheckCircleOutlined />} color="success">
                            {t("label:cards.is_enrolled")}
                        </Tag>
                    </div>
                ) : (
                    <div className="flex items-left justify-left">
                        <Tag className="flex items-left" icon={<CloseCircleOutlined />} color="warning">
                            {t("label:cards.not_enrolled")}
                        </Tag>
                    </div>
                )
            },
        }),
        ...insertIf(loggedInUser.activeCompanyProfile.isAdmin && !isFreeVersion, {
            title: t("label:current_expenses"),
            key: "expenses",
            width: 150,
            align: "right",
            className: "pr-10",
            sortOrder: getSortOrderForColumn(employeesTable, "expenses"),
            sorter: (employeeA: EmployeeWithExpenseData, employeeB: EmployeeWithExpenseData) => {
                if (employeeA.expenseData && employeeB.expenseData) return employeeA.expenseData.value - employeeB.expenseData.value
                else return 0
            },
            render: (employee: EmployeeWithExpenseData) => {
                if (!employee.expenseData) return <LoadingData />
                const expensesSoFar = employee.expenseData?.value || 0
                return <PriceLabel value={expensesSoFar} currency={loggedInUser.settings.globalCurrency} interval={PriceIntervalEnum.ONE_TIME} />
            },
        }),
    ]

    const handleNewEmployee = () => showModal(EmployeeModal, false, { isShowing: true, isNew: true })

    return (
        <div>
            <Helmet>
                <title>{t("view:employees.title")}</title>
            </Helmet>
            <div className="flex gap-10 justify-between items-center mb-16">
                <h1>{t("view:employees.title")}</h1>
                <div className="flex flex-row space-x-10">
                    {(employeesTable.data.totalDocs > 0 || employeesTable.data.search) && (
                        <SearchInput className="min-w-32" onSearch={(searchString) => handleSearch.callback(searchString)} value={employeesTable.data.search} />
                    )}

                    <Filter
                        table={TablesEnum.EMPLOYEES}
                        isFilterModalShowing={isFilterModalShowing}
                        setIsFilterModalShowing={setIsFilterModalShowing}
                        onFilter={(data) => applyFilter(TablesEnum.EMPLOYEES, data)}
                        options={{ teams, locations, employees }}
                    />

                    {loggedInUser?.activeCompanyProfile.isAdmin && (
                        <div className="flex gap-10 flex-wrap">
                            {hasUnenrolledEmployees && loggedInUser?.activeCompanyProfile.weavrData?.userId && (
                                <Button className="min-w-120" onClick={enrollAll} loading={isEnrolling}>
                                    <UserIcon />
                                    <span>{t("creditCard:action.enroll_all_users")}</span>
                                </Button>
                            )}
                            {(employeesTable.data.totalDocs > 0 || employeesTable.data.search) && (
                                <Button type="primary" onClick={handleNewEmployee} icon={<PlusOutlined />}>
                                    <span>{t("action:employee:create")}</span>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <FilterSection table={TablesEnum.EMPLOYEES} options={{ teams, locations, employees }} />

            {pageLoading ? (
                <Loading />
            ) : employeesTable.error ? (
                <ErrorTable onTableReload={() => dispatch(refetchTable(TablesEnum.SUBSCRIPTIONS))} isLoading={employeesTable.isFetching} />
            ) : employeesTable.data.totalDocs === 0 && !employeesTable.isFetching ? (
                employeesTable.data.search ? (
                    <div className="animation-appear">
                        <NoSearchDataFound />
                    </div>
                ) : (
                    <>
                        {loggedInUser.activeCompanyProfile.isAdmin && !isFreeVersion && !isTableFilterApplied(employeesTable.data.filter) && <DataUploadAlert />}

                        <Empty
                            className="animation-appear"
                            image={`./icons/empty_table.svg`}
                            imageStyle={{
                                height: 150,
                                marginRight: "auto",
                                marginLeft: "auto",
                                marginTop: "15%",
                                marginBottom: "40px",
                                display: "inline-block",
                            }}
                            description={
                                <span className="text-lg font-bold mt-2 mb-0">
                                    {!employeesTable.data.filter ? t("info:empty_state.employees.title") : t("info:no_filter_data_found.title.employee")}
                                </span>
                            }
                        >
                            <div className="flex flex-col justify-between items-center">
                                {loggedInUser.activeCompanyProfile.isAdmin ? (
                                    <>
                                        <p>
                                            {!isTableFilterApplied(employeesTable.data.filter) ? t("info:empty_state.employees.message") : t("info:no_filter_data_found.message")}
                                        </p>
                                        <Button className="mt-14" type="primary" onClick={handleNewEmployee} icon={<PlusOutlined />}>
                                            <span>{t("action:employee:create")}</span>
                                        </Button>
                                    </>
                                ) : (
                                    <p>
                                        {!isTableFilterApplied(employeesTable.data.filter)
                                            ? t("info:empty_state.employees.message")
                                            : t("info:no_filter_data_found.message_approver")}
                                    </p>
                                )}
                            </div>
                        </Empty>
                    </>
                )
            ) : (
                <div>
                    <div className="overflow-auto p-2 animation-appear">
                        <Table<Employee>
                            rowKey={(record) => record.id}
                            columns={columns}
                            dataSource={employeesTable.data.docs} // this table do not use table.data.docs but the docs from the employee store
                            onChange={updateTableOnChange(TablesEnum.EMPLOYEES, employeesTable.data, false)}
                            pagination={{
                                position: ["bottomRight"],
                                showSizeChanger: true,
                                current: employeesTable.data.search ? 1 : employeesTable.data.page,
                                pageSize: employeesTable.data.limit,
                                hideOnSinglePage: false,
                                pageSizeOptions: ["5", "10", "20", "50", "100"],
                            }}
                            loading={{
                                spinning: employeesTable.isFetching,
                                indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                            }}
                            onRow={(employee: Employee) => {
                                return {
                                    onClick: () => {
                                        dispatch(setEmployee(employee))
                                        history.push(`/employees/${employee.id}`)
                                    },
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default Employees
